import { Bus } from '../game/audio/AudioEngine';
import { volumeSliders } from './SoundSettings';
import { viewSwitches } from './ViewSettings';
import { cameraSwitches } from './CameraSettings';
import { APP_VERSION } from '../version';

/**
 * Menu pod Escape: pauza, ustawienia, credits, wyjście.
 *
 * The classic arrangement, and it is classic because it works: the game dims
 * behind it and a short column of buttons sits in the middle. Nothing here is
 * novel and nothing should be — this is the screen somebody reaches for when
 * they want to stop looking at the game, and it has to be where they expect.
 *
 * **The game keeps running behind it.** Dimming is not pausing, and it cannot
 * be: other players walk around a world this client does not own, and a client
 * that stopped its own clock would come back to a world that had moved on
 * without it. What the overlay does is take the input — it covers the canvas, so
 * no click reaches the map, and the camera is told to ignore the keyboard.
 *
 * **Escape goes back one step, not out.** From the settings it returns to the
 * buttons; from the buttons it closes. A key that always closes everything
 * makes leaving a submenu a two-key affair, and one that never closes traps
 * whoever opened it by accident.
 */

export interface Credit {
  /** What this is: a track, a wood, a footstep. */
  what: string;
  author?: string;
  license?: string;
  source?: string;
}

type View = 'main' | 'settings' | 'credits';

export class GameMenu {
  private readonly root = document.createElement('div');
  private readonly box = document.createElement('div');
  private readonly title = document.createElement('div');
  private readonly body = document.createElement('div');
  private view: View = 'main';

  constructor(
    container: HTMLElement,
    private readonly deps: {
      onVolume: (bus: Bus, value: number) => void;
      onClouds: (wanted: boolean) => void;
      onSnap: (wanted: boolean) => void;
      /** Read when the credits are opened, so a late manifest still shows up. */
      credits: () => Credit[];
      onLogout: () => void;
      /** Told whenever the menu opens or closes, for whatever has to stand down. */
      onOpenChange?: (open: boolean) => void;
    }
  ) {
    this.root.className = 'pause';
    this.root.hidden = true;

    this.title.className = 'pause-title';
    this.body.className = 'pause-body';
    this.box.className = 'pause-box';
    this.box.append(this.title, this.body);
    this.root.appendChild(this.box);

    // A click on the dimmed background is a click outside the menu, which is
    // the other thing everybody tries. Clicks inside must not reach it.
    this.root.addEventListener('mousedown', (event) => {
      if (event.target === this.root) this.hide();
    });
    this.box.addEventListener('mousedown', (event) => event.stopPropagation());

    container.appendChild(this.root);
  }

  get open(): boolean {
    return !this.root.hidden;
  }

  toggle(): void {
    if (this.open) this.hide();
    else this.show();
  }

  show(): void {
    if (this.open) return;
    this.view = 'main';
    this.paint();
    this.root.hidden = false;
    this.deps.onOpenChange?.(true);
  }

  hide(): void {
    if (!this.open) return;
    this.root.hidden = true;
    this.body.replaceChildren();
    this.deps.onOpenChange?.(false);
  }

  /**
   * Answer Escape, and say whether it was answered.
   *
   * The caller needs to know, because the same key means four other things in
   * this game and the order between them is the whole point.
   */
  escape(): boolean {
    if (!this.open) return false;
    if (this.view === 'main') {
      this.hide();
      return true;
    }
    this.view = 'main';
    this.paint();
    return true;
  }

  dispose(): void {
    this.root.remove();
  }

  private paint(): void {
    this.title.textContent =
      this.view === 'settings' ? 'Ustawienia' : this.view === 'credits' ? 'Credits' : 'Pauza';

    this.body.replaceChildren(
      this.view === 'settings'
        ? this.settings()
        : this.view === 'credits'
          ? this.credits()
          : this.buttons()
    );
  }

  private buttons(): HTMLElement {
    const list = document.createElement('div');
    list.className = 'pause-buttons';

    // Continue first: it is what most openings of this menu end with, including
    // every one that was an accident.
    list.append(
      this.action('Kontynuuj', () => this.hide()),
      this.action('Ustawienia', () => this.go('settings')),
      this.action('Credits', () => this.go('credits')),
      this.action('Wyloguj', () => this.deps.onLogout(), 'pause-quit')
    );

    const version = document.createElement('div');
    version.className = 'pause-version';
    version.textContent = `wersja ${APP_VERSION}`;
    list.appendChild(version);
    return list;
  }

  private settings(): HTMLElement {
    const wrap = document.createElement('div');
    wrap.className = 'pause-view';

    const sound = document.createElement('div');
    sound.className = 'pause-heading';
    sound.textContent = 'Dźwięk';

    const view = document.createElement('div');
    view.className = 'pause-heading pause-heading-later';
    view.textContent = 'Pogoda i czas';

    const camera = document.createElement('div');
    camera.className = 'pause-heading pause-heading-later';
    camera.textContent = 'Kamera';

    wrap.append(
      sound,
      volumeSliders(this.deps.onVolume),
      view,
      viewSwitches(this.deps.onClouds),
      camera,
      cameraSwitches(this.deps.onSnap),
      this.back()
    );
    return wrap;
  }

  private credits(): HTMLElement {
    const wrap = document.createElement('div');
    wrap.className = 'pause-view';

    const lines = this.deps.credits();
    if (lines.length === 0) {
      const empty = document.createElement('div');
      empty.className = 'pause-empty';
      // Honest rather than blank: there is nothing to credit because there is
      // nothing here yet, and those are different from a list that failed.
      empty.textContent = 'nie ma jeszcze czego wymienić';
      wrap.append(empty, this.back());
      return wrap;
    }

    const list = document.createElement('div');
    list.className = 'pause-credits';
    for (const credit of lines) {
      const row = document.createElement('div');
      row.className = 'pause-credit';

      const what = document.createElement('span');
      what.className = 'pause-credit-what';
      what.textContent = credit.what;

      const by = document.createElement('span');
      by.className = 'pause-credit-by';
      by.textContent = [credit.author, credit.license].filter(Boolean).join(' · ');

      row.append(what, by);

      if (credit.source) {
        const link = document.createElement('a');
        link.className = 'pause-credit-link';
        link.href = credit.source;
        link.target = '_blank';
        link.rel = 'noreferrer noopener';
        link.textContent = 'źródło';
        row.appendChild(link);
      }

      list.appendChild(row);
    }

    wrap.append(list, this.back());
    return wrap;
  }

  private back(): HTMLElement {
    return this.action('Wróć', () => this.go('main'), 'pause-back');
  }

  private go(view: View): void {
    this.view = view;
    this.paint();
  }

  private action(label: string, run: () => void, extra?: string): HTMLElement {
    const button = document.createElement('button');
    button.type = 'button';
    button.className = extra ? `pause-action ${extra}` : 'pause-action';
    button.textContent = label;
    button.addEventListener('click', run);
    return button;
  }
}
